import { useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, Chip, Divider, Grid, InputAdornment,
  Stack, TextField, Typography
} from '@mui/material';
import { BadgeOutlined, QrCodeScanner, Replay } from '@mui/icons-material';
import { api } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import { formatApiError, useToast } from '../contexts/ToastContext.jsx';

function successTone() {
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.setValueAtTime(660, context.currentTime);
  oscillator.frequency.setValueAtTime(880, context.currentTime + 0.12);
  gain.gain.setValueAtTime(0.12, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.3);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.3);
}

function errorTone() {
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = 'square';
  oscillator.frequency.setValueAtTime(220, context.currentTime);
  gain.gain.setValueAtTime(0.08, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.35);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.35);
}

function money(value) {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function StitcherScanPage() {
  const toast = useToast();
  const rfidRef = useRef();
  const orderRef = useRef();
  const [employee, setEmployee] = useState(null);
  const [scannedRfid, setScannedRfid] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    rfidRef.current?.focus();
  }, []);

  const resetForNext = () => {
    setEmployee(null);
    setScannedRfid('');
    setBusy(false);
    if (rfidRef.current) rfidRef.current.value = '';
    if (orderRef.current) orderRef.current.value = '';
    setTimeout(() => rfidRef.current?.focus(), 50);
  };

  const scanEmployee = async (event) => {
    if (event.key !== 'Enter' || !event.currentTarget.value.trim() || busy) return;
    const rfid = event.currentTarget.value.trim();
    event.currentTarget.value = '';
    setBusy(true);
    try {
      const { data } = await api.get(`/employees/by-rfid/${encodeURIComponent(rfid)}`, { skipErrorToast: true });
      setEmployee(data);
      setScannedRfid(rfid);
      setLastResult(null);
      successTone();
      toast.success(`${data.name} ready — scan order barcode`);
      setTimeout(() => orderRef.current?.focus(), 50);
    } catch (error) {
      setEmployee(null);
      setScannedRfid('');
      errorTone();
      toast.error(formatApiError(error, 'RFID scan failed'));
      setTimeout(() => rfidRef.current?.focus(), 50);
    } finally {
      setBusy(false);
    }
  };

  const scanOrder = async (event) => {
    if (event.key !== 'Enter' || !event.currentTarget.value.trim() || busy) return;
    const orderNumber = event.currentTarget.value.trim();
    event.currentTarget.value = '';

    if (!employee || !scannedRfid) {
      errorTone();
      toast.warning('Pehle employee ka RFID card scan karein');
      setTimeout(() => rfidRef.current?.focus(), 50);
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post('/production/stitcher-scan', {
        rfid: scannedRfid,
        orderNumber
      }, { skipErrorToast: true });

      setLastResult(data);
      successTone();
      toast.success(
        `Order ${data.order.orderNumber} → ${data.employee.name} · Rs ${money(data.payment.amount)}`
      );
      setEmployee(null);
      setScannedRfid('');
      setTimeout(() => rfidRef.current?.focus(), 80);
    } catch (error) {
      errorTone();
      toast.error(formatApiError(error, 'Order assign nahi ho saka'));
      setTimeout(() => orderRef.current?.focus(), 50);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Stitcher Scan"
        subtitle="Step 2 — RFID + order barcode se job stitcher ke khate mein shift hoti hai"
        action={(
          <Button startIcon={<Replay />} variant="outlined" onClick={resetForNext} disabled={busy}>
            Reset
          </Button>
        )}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ p: 2.5, height: '100%' }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label="1" color="primary" size="small" />
                <Typography fontWeight={700}>Employee RFID</Typography>
              </Stack>
              <TextField
                inputRef={rfidRef}
                fullWidth
                size="small"
                label="RFID card scan"
                placeholder="Card scan karein — Enter"
                disabled={busy}
                onKeyDown={scanEmployee}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start"><BadgeOutlined /></InputAdornment>
                    )
                  }
                }}
              />

              {employee ? (
                <Alert severity="success">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar src={employee.photoPath || undefined}>{employee.name?.[0]}</Avatar>
                    <Box>
                      <Typography fontWeight={700}>{employee.name}</Typography>
                      <Typography variant="body2">
                        {employee.employeeCode} · {employee.department} · {employee.designation}
                      </Typography>
                    </Box>
                  </Stack>
                </Alert>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  Pehle stitcher apna RFID card scan kare.
                </Typography>
              )}

              <Divider />

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label="2" color={employee ? 'primary' : 'default'} size="small" />
                <Typography fontWeight={700}>Order barcode (3&quot; slip)</Typography>
              </Stack>
              <TextField
                inputRef={orderRef}
                fullWidth
                size="small"
                label="Order barcode scan"
                placeholder="Slip ka barcode scan karein — Enter"
                disabled={busy || !employee}
                onKeyDown={scanOrder}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start"><QrCodeScanner /></InputAdornment>
                    )
                  }
                }}
              />
              <Typography color="text.secondary" variant="body2">
                Barcode scan hote hi order is employee ke khate mein chala jata hai,
                aur Stitcher Rate × QTY payment ledger mein Credit hoti hai.
              </Typography>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ p: 2.5, height: '100%', bgcolor: 'action.hover' }}>
            <Typography fontWeight={700} mb={1.5}>Last assignment</Typography>
            {!lastResult ? (
              <Typography color="text.secondary">
                Abhi koi scan complete nahi hua. RFID → Order barcode.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                <Alert severity="success">
                  Order <strong>{lastResult.order.orderNumber}</strong> assigned to{' '}
                  <strong>{lastResult.employee.name}</strong>
                </Alert>
                <Box>
                  <Typography variant="body2" color="text.secondary">Bill Book No.</Typography>
                  <Typography fontWeight={650}>{lastResult.order.billBookNo || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Items / Qty</Typography>
                  <Typography fontWeight={650}>
                    {lastResult.order.itemCount} lines · {lastResult.order.totalQuantity} pcs
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Stitcher payment (Credit)</Typography>
                  <Typography fontWeight={750} fontSize="1.25rem">
                    Rs {money(lastResult.payment.amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ledger balance: Rs {money(lastResult.payment.balance)}
                  </Typography>
                </Box>
                {!!lastResult.jobs?.length && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>Jobs</Typography>
                    {lastResult.jobs.map((job) => (
                      <Typography key={job.id} variant="body2">
                        {job.jobNumber} · {job.itemName} · Qty {job.quantity} · Rs {money(job.amount)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
